import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Check,
    ChevronRight,
    Monitor,
    Users,
    Building2,
    ShieldAlert,
    HelpCircle,
    Folder,
    UserCheck,
    X,
    Clock,
} from "lucide-react";
import * as Icons from "lucide-react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { FormRegistry } from "../forms/FormRegistry";
import { AttachmentBar } from "../shared/AttachmentBar";
import { TICKET_TYPES, getTicketTypeConfig, cn } from "../../lib/utils";
import { PageLoader } from "../shared/LoadingSpinner";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import KBSuggestions from "../shared/KBSuggestions";
import { toast } from "sonner";

const ICON_MAP = {
    Monitor,
    Users,
    Building2,
    ShieldAlert,
    HelpCircle,
    Folder,
};

const CATEGORY_COLORS = [
    "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100",
    "bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100",
    "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100",
    "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100",
    "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100",
];

const CATEGORY_ICON_COLORS = [
    "bg-blue-100 text-blue-600",
    "bg-purple-100 text-purple-600",
    "bg-amber-100 text-amber-600",
    "bg-emerald-100 text-emerald-600",
    "bg-gray-100 text-gray-500",
];

export function NewTicket() {
    const navigate = useNavigate();
    const { currentUser, users, departments, getDepartmentById, hasPermission } = useApp();
    const [step, setStep] = useState(1);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [createdTicket, setCreatedTicket] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    // Demandeur (only for agents/admins)
    const [requester, setRequester] = useState(null);
    // Bénéficiaire
    const [beneficiary, setBeneficiary] = useState(null);
    const [beneficiarySearch, setBeneficiarySearch] = useState("");
    // Pending attachments for ticket creation
    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    // KB suggestions
    const [formTitle, setFormTitle] = useState("");

    const isITStaff = hasPermission("agent");
    const isCoordinatorOrAbove = hasPermission("coordinator");
    // Priority restriction for regular users
    const allowedPriorities = isITStaff ? undefined : ["low", "medium", "high"];

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            const data = await api.getTicketCategories();
            setCategories(data);
        } catch (err) {
            console.error("Failed to load ticket categories:", err);
            toast.error("Erreur lors du chargement des catégories");
        } finally {
            setLoading(false);
        }
    };

    // Deadline
    const [deadline, setDeadline] = useState("");

    // Filter users for beneficiary search — role determines scope
    const beneficiaryCandidates = useMemo(() => {
        const activeUsers = users.filter((u) => u.is_active !== false && u.id !== currentUser?.id);
        if (isCoordinatorOrAbove) {
            return activeUsers;
        }
        // Users: same departments (primary + secondary)
        const myDepts = new Set(
            [currentUser?.department_id, ...(currentUser?.secondary_department_ids || [])].filter(Boolean),
        );
        return activeUsers.filter(
            (u) => myDepts.has(u.department_id) || (u.secondary_department_ids || []).some((d) => myDepts.has(d)),
        );
    }, [users, currentUser, isCoordinatorOrAbove]);

    const getIcon = (iconName) => {
        return ICON_MAP[iconName] || Icons[iconName] || HelpCircle;
    };

    const getTypesForCategory = (cat) => {
        if (!cat?.ticket_types) return [];
        return cat.ticket_types
            .map((typeId) => TICKET_TYPES.find((t) => t.id === typeId))
            .filter(Boolean);
    };

    const handleCategorySelect = (cat) => {
        setSelectedCategory(cat);
        setStep(2);
    };

    const handleTypeSelect = (typeId) => {
        setSelectedType(typeId);
        setStep(3);
    };

    const handleFormSubmit = async (formData) => {
        try {
            setSubmitting(true);
            const typeConfig = getTicketTypeConfig(selectedType);

            // Determine effective requester and beneficiary
            const effectiveRequester = requester || currentUser;
            const effectiveBeneficiary = beneficiary || effectiveRequester;

            // Department: auto from beneficiary or requester or currentUser
            const deptId = effectiveBeneficiary.department_id
                || effectiveRequester.department_id
                || currentUser.department_id;

            const ticketData = {
                type: selectedType,
                title: formData.title,
                description: formData.description || "",
                priority: formData.priority || typeConfig.defaultPriority,
                category: selectedCategory?.name || null,
                requester_id: isCoordinatorOrAbove ? effectiveRequester.id : currentUser.id,
                beneficiary_id: effectiveBeneficiary.id,
                department_id: deptId,
                deadline: deadline ? new Date(deadline).toISOString() : null,
                dynamic_fields: formData.dynamic_fields || {},
            };

            const ticket = await api.createTicket(ticketData);

            // Upload pending files
            if (pendingFiles.length > 0) {
                setUploadingFiles(true);
                for (const pf of pendingFiles) {
                    try {
                        await api.uploadAttachment(ticket.id, pf.file, currentUser.id);
                    } catch (err) {
                        console.error("Failed to upload file:", pf.file.name, err);
                    }
                }
                setUploadingFiles(false);
            }

            setCreatedTicket(ticket);
            setStep(4);
            toast.success(
                pendingFiles.length > 0
                    ? `Ticket créé avec ${pendingFiles.length} pièce(s) jointe(s)`
                    : "Ticket créé avec succès"
            );
        } catch (error) {
            console.error("Failed to create ticket:", error);
            toast.error("Erreur lors de la création du ticket");
        } finally {
            setSubmitting(false);
            setUploadingFiles(false);
        }
    };

    const handleBack = () => {
        if (step === 3) {
            setStep(2);
            setSelectedType(null);
        } else if (step === 2) {
            setStep(1);
            setSelectedCategory(null);
        }
    };

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="new-ticket">
            {/* Step 1: Category Selection */}
            {step === 1 && (
                <>
                    <div className="page-header">
                        <h1 className="page-title">Nouveau ticket</h1>
                        <p className="page-subtitle">
                            Quel service concerne votre demande ?
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                        {categories.map((cat, idx) => {
                            const Icon = getIcon(cat.icon);
                            const colorIdx = idx % CATEGORY_COLORS.length;
                            const types = getTypesForCategory(cat);

                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => handleCategorySelect(cat)}
                                    className={cn(
                                        "group relative flex flex-col items-start p-6 rounded-xl border-2 transition-all duration-200 text-left",
                                        CATEGORY_COLORS[colorIdx]
                                    )}
                                    data-testid={`category-${cat.id}`}
                                >
                                    <div
                                        className={cn(
                                            "w-12 h-12 rounded-xl flex items-center justify-center mb-4",
                                            CATEGORY_ICON_COLORS[colorIdx]
                                        )}
                                    >
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="text-base font-semibold mb-1">
                                        {cat.name}
                                    </h3>
                                    <p className="text-sm opacity-70 mb-3">
                                        {cat.description}
                                    </p>
                                    <span className="text-xs opacity-50">
                                        {types.length} type{types.length > 1 ? "s" : ""} de ticket
                                    </span>
                                    <ChevronRight className="w-5 h-5 absolute top-6 right-4 opacity-30 group-hover:opacity-70 transition-opacity" />
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Step 2: Type Selection (filtered by category) */}
            {step === 2 && selectedCategory && (
                <>
                    {/* Breadcrumb */}
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                        <button
                            onClick={() => { setStep(1); setSelectedCategory(null); }}
                            className="hover:text-gray-900 transition-colors"
                            data-testid="breadcrumb-back"
                        >
                            Nouveau ticket
                        </button>
                        <ChevronRight className="w-3.5 h-3.5" />
                        <span className="text-gray-900 font-medium">
                            {selectedCategory.name}
                        </span>
                    </div>

                    <button
                        onClick={handleBack}
                        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-4 transition-colors"
                        data-testid="back-to-categories"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Choisir une autre catégorie
                    </button>

                    <div className="page-header">
                        <h1 className="page-title">
                            {selectedCategory.name}
                        </h1>
                        <p className="page-subtitle">
                            Sélectionnez le type de demande
                        </p>
                    </div>

                    <div className="ticket-type-grid mt-4">
                        {getTypesForCategory(selectedCategory).map((type) => {
                            const TypeIcon = Icons[type.icon] || HelpCircle;
                            return (
                                <button
                                    key={type.id}
                                    onClick={() => handleTypeSelect(type.id)}
                                    className="ticket-type-card text-left"
                                    data-testid={`type-${type.id}`}
                                >
                                    <div
                                        className={cn(
                                            "ticket-type-icon",
                                            type.defaultPriority === "critical"
                                                ? "bg-red-100 text-red-600"
                                                : type.defaultPriority === "high"
                                                ? "bg-orange-100 text-orange-600"
                                                : type.category === "hr"
                                                ? "bg-purple-100 text-purple-600"
                                                : "bg-blue-100 text-blue-600"
                                        )}
                                    >
                                        <TypeIcon className="w-5 h-5" />
                                    </div>
                                    <div className="ticket-type-name">
                                        {type.label}
                                    </div>
                                    <div className="ticket-type-description">
                                        {type.description}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Step 3: Dynamic Form */}
            {step === 3 && selectedType && (
                <>
                    {/* Breadcrumb */}
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                        <button
                            onClick={() => { setStep(1); setSelectedCategory(null); setSelectedType(null); }}
                            className="hover:text-gray-900 transition-colors"
                        >
                            Nouveau ticket
                        </button>
                        <ChevronRight className="w-3.5 h-3.5" />
                        <button
                            onClick={() => { setStep(2); setSelectedType(null); }}
                            className="hover:text-gray-900 transition-colors"
                        >
                            {selectedCategory?.name}
                        </button>
                        <ChevronRight className="w-3.5 h-3.5" />
                        <span className="text-gray-900 font-medium">
                            {getTicketTypeConfig(selectedType).label}
                        </span>
                    </div>

                    <button
                        onClick={handleBack}
                        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-4 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Changer de type
                    </button>

                    <div className="max-w-3xl">
                        <div className="page-header">
                            <div className="flex items-center gap-3 mb-2">
                                {(() => {
                                    const type = getTicketTypeConfig(selectedType);
                                    const Icon = Icons[type.icon] || HelpCircle;
                                    return (
                                        <>
                                            <div
                                                className={cn(
                                                    "ticket-type-icon",
                                                    type.defaultPriority === "critical"
                                                        ? "bg-red-100 text-red-600"
                                                        : type.defaultPriority === "high"
                                                        ? "bg-orange-100 text-orange-600"
                                                        : "bg-blue-100 text-blue-600"
                                                )}
                                            >
                                                <Icon className="w-5 h-5" />
                                            </div>
                                            <span className="text-lg font-medium">
                                                {type.label}
                                            </span>
                                        </>
                                    );
                                })()}
                            </div>
                            <h1 className="page-title">Créer un ticket</h1>
                        </div>

                        {/* Demandeur + Bénéficiaire */}
                        <div className="card mb-4" data-testid="people-section">
                            <div className="card-body space-y-4">
                                {/* Demandeur — only for agents/admins/coordinators */}
                                {isCoordinatorOrAbove && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <UserCheck className="w-4 h-4 text-gray-400" />
                                            <span className="text-sm font-medium text-gray-700">Demandeur</span>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">La personne qui fait la demande. Si vide, ce sera vous.</p>
                                        {!requester ? (
                                            <UserSearchSelect
                                                users={users.filter((u) => u.is_active !== false)}
                                                departments={departments}
                                                value={null}
                                                onChange={async (userId) => {
                                                    if (userId) {
                                                        let u = users.find((x) => x.id === userId);
                                                        if (!u) { try { u = await api.getUser(userId); } catch (err) { console.warn("Chargement utilisateur impossible:", err); } }
                                                        if (u) setRequester(u);
                                                    }
                                                }}
                                                placeholder="Rechercher le demandeur..."
                                                excludeUserIds={[currentUser?.id]}
                                                data-testid="requester-search"
                                            />
                                        ) : (
                                            <SelectedPersonBadge person={requester} color="blue" onRemove={() => setRequester(null)} testId="selected-requester" getDepartmentById={getDepartmentById} />
                                        )}
                                    </div>
                                )}

                                {/* Bénéficiaire — for everyone */}
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <UserCheck className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm font-medium text-gray-700">
                                            {isCoordinatorOrAbove ? "Bénéficiaire" : "Cette demande concerne"}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-2">
                                        {isCoordinatorOrAbove
                                            ? "La personne concernée. Si vide, ce sera le demandeur."
                                            : "Laissez vide si la demande est pour vous-même."}
                                    </p>
                                    {!beneficiary ? (
                                        <UserSearchSelect
                                            users={beneficiaryCandidates}
                                            departments={departments}
                                            value={null}
                                            onChange={async (userId) => {
                                                if (userId) {
                                                    let u = users.find((x) => x.id === userId);
                                                    if (!u) { try { u = await api.getUser(userId); } catch (err) { console.warn("Chargement utilisateur impossible:", err); } }
                                                    if (u) setBeneficiary(u);
                                                }
                                            }}
                                            placeholder={isCoordinatorOrAbove ? "Rechercher le bénéficiaire..." : "Rechercher un collègue..."}
                                            excludeUserIds={[currentUser?.id]}
                                            allowCreateExternal={isITStaff}
                                            data-testid="beneficiary-search"
                                        />
                                    ) : (
                                        <SelectedPersonBadge person={beneficiary} color="green" onRemove={() => { setBeneficiary(null); setBeneficiarySearch(""); }} testId="selected-beneficiary" getDepartmentById={getDepartmentById} />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* KB Suggestions */}
                        <KBSuggestions
                            title={formTitle}
                            ticketType={selectedType}
                        />

                        {/* Deadline field */}
                        <div className="card mb-4" data-testid="deadline-section">
                            <div className="card-body">
                                <div className="flex items-center gap-3 mb-2">
                                    <Clock className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-700">Date limite souhaitée</span>
                                    <span className="text-xs text-gray-400">(optionnel)</span>
                                </div>
                                <p className="text-xs text-gray-500 mb-3">
                                    {isITStaff
                                        ? "Définissez une date limite pour ce ticket. Si vide, la deadline sera calculée automatiquement selon la priorité."
                                        : "Indiquez la date à laquelle vous souhaitez que votre demande soit traitée."}
                                </p>
                                <input
                                    type="datetime-local"
                                    value={deadline}
                                    onChange={(e) => setDeadline(e.target.value)}
                                    min={(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })()}
                                    className="w-full sm:w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    data-testid="deadline-input"
                                />
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-body">
                                <FormRegistry
                                    ticketType={selectedType}
                                    onSubmit={handleFormSubmit}
                                    submitting={submitting || uploadingFiles}
                                    onTitleChange={setFormTitle}
                                    allowedPriorities={allowedPriorities}
                                    onTypeChange={(newType) => {
                                        // Find the category for this type
                                        const typeConfig = getTicketTypeConfig(newType);
                                        if (typeConfig) {
                                            const cat = categories.find((c) => c.id === typeConfig.category);
                                            if (cat) setSelectedCategory(cat);
                                            setSelectedType(newType);
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        {/* Attachment bar */}
                        <div className="card mt-4" data-testid="create-ticket-attachments">
                            <div className="card-body">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">Pièces jointes</h3>
                                <AttachmentBar
                                    pendingFiles={pendingFiles}
                                    setPendingFiles={setPendingFiles}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && createdTicket && (
                <div className="max-w-lg mx-auto text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Ticket créé avec succès
                    </h1>
                    <p className="text-4xl font-mono font-bold text-gray-900 mb-4">
                        {createdTicket.ticket_number}
                    </p>
                    <p className="text-gray-600 mb-2">{createdTicket.title}</p>
                    <p className="text-sm text-gray-500 mb-8">
                        Temps de réponse estimé :{" "}
                        {createdTicket.priority === "critical"
                            ? "1 heure"
                            : createdTicket.priority === "high"
                            ? "4 heures"
                            : createdTicket.priority === "medium"
                            ? "8 heures"
                            : "24 heures"}
                    </p>
                    <div className="flex items-center justify-center gap-4">
                        <Button
                            variant="outline"
                            onClick={() => navigate("/tickets")}
                        >
                            Voir mes tickets
                        </Button>
                        <Button onClick={() => navigate(`/tickets/${createdTicket.id}`)}>
                            Voir le ticket
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function SelectedPersonBadge({ person, color, onRemove, testId, getDepartmentById }) {
    const bgColors = { blue: "bg-blue-50 border-blue-200", green: "bg-green-50 border-green-200" };
    const textColors = { blue: "text-blue-700", green: "text-green-700" };
    const avatarColors = { blue: "bg-blue-200 text-blue-700", green: "bg-green-200 text-green-700" };
    const subColors = { blue: "text-blue-600", green: "text-green-600" };
    const btnColors = { blue: "text-blue-400 hover:text-blue-600", green: "text-green-400 hover:text-green-600" };
    return (
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${bgColors[color]}`} data-testid={testId}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${avatarColors[color]}`}>
                {person.first_name?.[0]}{person.last_name?.[0]}
            </div>
            <div className="flex-1">
                <div className={`text-sm font-semibold ${textColors[color]}`}>{person.first_name} {person.last_name}</div>
                <div className={`text-xs ${subColors[color]}`}>
                    {person.email}{(() => { const d = getDepartmentById(person.department_id); return d ? ` — ${d.name}` : ""; })()}
                </div>
            </div>
            <button type="button" onClick={onRemove} className={`p-1 ${btnColors[color]}`} data-testid={`remove-${testId}-btn`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
        </div>
    );
}

